import { DicomInfoFactory } from '@/lib/dicom/DicomInfoFactory';
import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import type { DicomGroups } from '@/lib/dicom/DicomSeries';
import { DicomdirFilename } from '@/lib/dicom/dicomFileChecks';
import DicomUtils from '@/lib/dicom/DicomUtil';
import { logger } from '@/util/logger';
import { errorMessage } from '@/util/errorMessage';

const log = logger('groupDicomSeries');

/**
 * Series with fewer than this many DICOM images are filtered out during
 * pre-screening.
 */
const MIN_NUMBER_OF_IMAGES = 50;

function isScout(info: DicomInfo): boolean {
    return info.imageType.includes('LOCALIZER');
}

/**
 * Filename pre-filter — keep files that plausibly could be DICOM and drop the
 * obvious junk (Thumbs.db, .DS_Store, README.txt, image.jpg, the DICOMDIR
 * index, …) without paying the cost of parsing each one.
 *
 * Keep if:
 *   - the filename has no dot at all (`IM001`, `00001`, …), OR
 *   - the segment after the final dot is `dcm` (case-insensitive), OR
 *   - the segment after the final dot is all digits (UID-style names like
 *     `1.2.840.10008.5.1.4.1.1.2.123456`).
 *
 * Always drop `DICOMDIR` (case-insensitive) — it's the DICOM-folder index file,
 * not an image, and exclude it from upload.
 */
function isPlausiblyDicomFile(file: File): boolean {
    const name = file.name;
    if (name.toUpperCase() === DicomdirFilename) return false;

    const lastDot = name.lastIndexOf('.');
    if (lastDot < 0) return true;

    const ext = name.slice(lastDot + 1).toLowerCase();
    return ext === 'dcm' || /^\d+$/.test(ext);
}

/**
 * Pre-screen uploaded files and group by SeriesInstanceUID, returning one
 * {@link DicomSeries} per group — the same structure the
 * single series validation ({@link validateDicoms}) produces, so a single type flows
 * through pre-screen → selection → validation.
 *
 * Only the cheap filters run here: non-DICOM filenames, files the parser flags
 * as excluded, scout (localizer) images, files with no SeriesInstanceUID, and
 * whole series with fewer than {@link MIN_NUMBER_OF_IMAGES} files. Series below
 * that threshold are dropped entirely, so the user never sees them in the picker.
 */

export async function groupDicomSeries(files: File[]): Promise<DicomGroups> {
    const candidates = files.filter(isPlausiblyDicomFile);

    // Parse files independently — a single unreadable/corrupt file must not sink the
    // whole batch, so failures are dropped per-file rather than rejecting the lot.
    const settled = await Promise.allSettled(
        candidates.map((f) => DicomInfoFactory.makeDicomInfo(f)),
    );

    const parsed: DicomInfo[] = [];
    for (const result of settled) {
        if (result.status === 'fulfilled') {
            parsed.push(result.value);
        } else {
            log.warn(
                'Skipping unparseable file during series grouping: %s',
                errorMessage(result.reason),
            );
        }
    }

    const screened = parsed.filter(
        (info) => !info.isExcluded && info.seriesInstanceUid && !isScout(info),
    );

    const groups = DicomUtils.groupSeries(screened);

    for (const [uid, series] of Object.entries(groups)) {
        if (series.items.length < MIN_NUMBER_OF_IMAGES) delete groups[uid];
    }

    return groups;
}
